import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { AbstractElementCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to move an element to a new position on the timeline
 * Uses direct object references to elements and tracks
 */
export class ShiftElementCommand extends AbstractElementCommand {
  private newStartTime: number;

  getName(): string {
    return 'ShiftElementCommand';
  }
  private originalState: Record<string, any>;

  /**
   * Creates a new ShiftElementCommand
   * @param editor The video editor instance
   * @param element The element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @param targetTrack Optional target track if moving to a different track
   */
  constructor(editor: VideoEditor, element: Element, newStartTime: number) {
    super(editor, element);

    // Ensure time is never negative
    this.newStartTime = Math.max(0, this.snapTimeToFrame(newStartTime));

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Store affected elements for undo
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    let result = false;
    const isTranscriptElem = this.isTranscriptElement();
    if (isTranscriptElem) {
      result = this.shiftTranscriptElement();
    } else {
      result = this.shiftRegularElement();
    }

    if (result) {
      // Recalculate timeline duration after successful move
      this.recalculateTimeline();

      // Reconcile karaoke track if transcript element was shifted
      if (isTranscriptElem) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
      }
    }

    return result;
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Shifts a VideoTranscriptElement using TranscriptTrackManager
   */
  private shiftTranscriptElement(): boolean {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    const clipResult = transcriptTrackManager.shiftElement(
      this.sourceElement as VideoTranscriptElement,
      this.newStartTime,
      this.getTimelineMode(), // Pass the mode explicitly
    );
    return clipResult !== null;
  }

  /**
   * Shifts a regular element using TrackManager
   */
  private shiftRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.shiftElement(
      this.sourceElement,
      this.newStartTime,
      this.getTimelineMode(), // Pass the mode explicitly
    );
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Move element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }
}
